import type { Variant } from 'types';
import type { ExperimentWithVerdict } from '@@/api/experiments/implementation-verdict';
import { NavLink } from '@/components/NavLink';
import { DataTable, useDataControl } from '@/components/DataTable';
import { useImplementationList } from '@/state/experiments/useImplementationList';
import { ImplementationTopHeader } from './ImplementationTopHeader';
import { ImplementationActionsCell } from './ImplementationActionsCell';
import { ScreenshotsCell } from './ScreenshotsCell';
import { CodeModal } from './CodeModal';
import { EndOnImplementDialog } from './EndOnImplementDialog';
import { WinningVariantCell } from './WinningVariantCell';
import { resolveVariants, resolveCodeVariant } from './variant-selection';
import {
  sectionExperiments,
  filterExperiments,
  SECTION_META,
  SECTION_ORDER,
  type ImplementationSection,
} from './implementation-sections';
import { ExternalLink, Code2, Check, X } from 'lucide-react';
import { formatDateTime } from 'helpers/formatters';
import { cn } from 'helpers/cn';
import { Checkbox } from '@/components/ui/checkbox';
import { ImplementationEmptyState } from './ImplementationEmptyState';

/**
 * The implementation list, split by what it actually asks of the reader.
 *
 * A flat list conflated experiments needing work with experiments needing nothing,
 * which is the only question this page exists to answer. The queue is listed first so
 * it is not buried under rows that are there for reference.
 *
 * Each section gets its own DataTable and its own controller, so sorting and paging in
 * one does not disturb another.
 */
/**
 * Column heads are the one piece of the shared DataTable a screen can restyle, since
 * the table itself lives in `workspaces/` and is shared with the editor app.
 */
const HEAD = 'micro-label whitespace-nowrap';

/** Product tour anchors for the sections the tour points at. */
const SECTION_TOUR_ANCHOR: Partial<Record<ImplementationSection, string>> = {
  needsWork: 'implementation-queue',
  deployed: 'implementation-deployed',
  implemented: 'implementation-implemented',
};

type SectionTableProps = {
  section: ImplementationSection;
  /** Already filtered by the page's query, so the table only sorts and pages. */
  experiments: ExperimentWithVerdict[];
  /** Before filtering, so the badge still states how big the section really is. */
  total: number;
  searchQuery: string;
  /** Marks or unmarks the row; marking a live experiment first asks whether to end it. */
  requestImplemented: (experiment: ExperimentWithVerdict) => void;
  onShowCode: (variant: Variant) => void;
  /** This section's first row carries the row tour anchors: set on the first section with rows. */
  anchorFirstRow: boolean;
};

const SectionTable = ({
  section,
  experiments,
  total,
  searchQuery,
  requestImplemented,
  onShowCode,
  anchorFirstRow,
}: SectionTableProps) => {
  const meta = SECTION_META[section];
  // Sorting and paging only. The query is applied to the list before it gets here, so
  // there is no query to push into this controller and no effect to do it with.
  const dataController = useDataControl<ExperimentWithVerdict>(experiments);

  const hidden = searchQuery.trim().length > 0 && experiments.length === 0 && total > 0;
  const rowAnchor = (name: string, idx: number) => (anchorFirstRow && idx === 0 ? name : undefined);

  return (
    <section className="bg-panel border-line rounded-panel border" data-tour={SECTION_TOUR_ANCHOR[section]}>
      <div className="border-line flex items-start justify-between gap-3 border-b px-3 py-2.5">
        <div className="min-w-0 max-w-3xl space-y-1">
          <h2 className="text-body font-semibold tracking-tight">{meta.title}</h2>
          <p className="supporting">{meta.description}</p>
        </div>
        <span className="micro-label border-line rounded-pill shrink-0 border px-2 py-0.5 tracking-normal">
          {total}
        </span>
      </div>

      <div className="overflow-x-auto p-3">
        {total === 0 || hidden ? (
          <p className="supporting border-line rounded-card border border-dashed px-4 py-6 text-center">
            {hidden ? `No matches in this section for "${searchQuery}".` : meta.emptyMessage}
          </p>
        ) : (
          <DataTable<ExperimentWithVerdict>
            dataController={dataController}
            pageSize={10}
            columns={[
              {
                field: 'name',
                label: 'Experiment Name',
                sortKey: 'name',
                headerClassName: HEAD,
                cellClassName: 'min-w-[180px] max-w-[320px]',
                format: ({ item }) => (
                  <NavLink
                    to={`/experiments/${item.id}`}
                    className="hover:text-accent flex items-center gap-2 font-medium transition-colors duration-150"
                  >
                    <span className="min-w-0 truncate">{item.name}</span>
                    <ExternalLink className="text-dim size-3.5 shrink-0" strokeWidth={1.5} />
                  </NavLink>
                ),
              },
              {
                label: 'Winning Variant',
                headerClassName: cn(HEAD, 'w-48'),
                tooltip:
                  'The variant that beat the control, and the goal it won on. A deployed variant is shown as a rollout; where nothing won or is live, the variant that was tested.',
                format: ({ item, idx }) => (
                  <div data-tour={rowAnchor('implementation-winner', idx)}>
                    <WinningVariantCell experiment={item} />
                  </div>
                ),
              },
              {
                field: 'implementedAt',
                label: 'Implemented',
                headerClassName: cn(HEAD, 'w-28'),
                format: ({ item, idx }) => (
                  <div
                    className="flex justify-center"
                    onClick={(ev) => ev.stopPropagation()}
                    data-tour={rowAnchor('implementation-mark', idx)}
                  >
                    <Checkbox checked={!!item.implementedAt} onCheckedChange={() => requestImplemented(item)} />
                  </div>
                ),
              },
              {
                field: 'status',
                label: 'Status',
                headerClassName: cn(HEAD, 'w-24'),
                sortKey: 'status',
                format: ({ item }) => {
                  const isDeployed = item.status === 'Deployed';
                  return (
                    <span
                      className={cn(
                        'micro-label rounded-pill flex w-fit items-center gap-1 border px-2 py-0.5',
                        isDeployed ? 'border-accent-line text-accent' : 'border-line',
                      )}
                    >
                      {isDeployed ? (
                        <Check className="size-3 shrink-0" strokeWidth={1.5} />
                      ) : (
                        <X className="size-3 shrink-0" strokeWidth={1.5} />
                      )}
                      {item.status}
                    </span>
                  );
                },
              },
              {
                field: 'startsAt',
                label: 'Start Date',
                sortKey: 'startsAt',
                headerClassName: cn(HEAD, 'w-28'),
                cellClassName: 'num text-dim',
                format: ({ value }) => formatDateTime(value as number) || '-',
              },
              {
                field: 'endsAt',
                label: 'End Date',
                sortKey: 'endsAt',
                headerClassName: cn(HEAD, 'w-28'),
                cellClassName: 'num text-dim',
                format: ({ value }) => formatDateTime(value as number) || '-',
              },
              {
                label: 'Screenshots',
                headerClassName: cn(HEAD, 'w-32'),
                format: ({ item, idx }) => {
                  const { control, treatment } = resolveVariants(item);
                  return (
                    <ScreenshotsCell
                      controlScreenshots={control?.screenshots ?? []}
                      variantScreenshots={treatment?.screenshots ?? []}
                      tourAnchor={rowAnchor('implementation-screenshots', idx)}
                    />
                  );
                },
              },
              {
                label: 'Code',
                headerClassName: cn(HEAD, 'w-20'),
                format: ({ item, idx }) => {
                  const variant = resolveCodeVariant(item);
                  if (!variant) return <span className="supporting">-</span>;

                  return (
                    <button
                      type="button"
                      className="border-line text-dim hover:text-foreground hover:bg-hover flex size-8 items-center justify-center rounded-lg border bg-transparent transition-colors duration-150"
                      aria-label="Show the variant code"
                      data-tour={rowAnchor('implementation-code', idx)}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        onShowCode(variant);
                      }}
                    >
                      <Code2 className="size-3.5" strokeWidth={1.5} />
                    </button>
                  );
                },
              },
              {
                label: 'Actions',
                headerClassName: cn(HEAD, 'w-20'),
                cellClassName: 'w-20',
                format: ({ item, idx }) => (
                  <ImplementationActionsCell experiment={item} tourAnchor={rowAnchor('implementation-actions', idx)} />
                ),
              },
            ]}
          />
        )}
      </div>
    </section>
  );
};

export const ImplementationList = () => {
  const manager = useImplementationList();
  const { experiments, requestImplemented, searchQuery, codeModalVariant, showCode, hideCode } = manager;
  const { endPrompt, resolveEndPrompt, dismissEndPrompt } = manager;
  const { hasWebsite, isLoading, implementedOnly } = manager;

  // Section the full list, then filter each section. Splitting a paged list instead
  // would have sectioned only the first page.
  const sections = sectionExperiments(experiments);
  // Everything the page will actually show, in section order. The export follows the
  // page: a CSV that silently carries rows the reader cannot see is a different report.
  const visible = SECTION_ORDER.flatMap((section) => sections[section]);
  const shown = (section: ImplementationSection) => filterExperiments(sections[section], searchQuery);
  // One set of row anchors on the page, in the first section that has a row to put them on.
  const anchoredSection = SECTION_ORDER.find((section) => shown(section).length > 0);

  /*
    Distinguished before anything renders — the same three-states-as-one collapse the
    survey library had. With no website the query never runs (react-query reports
    isLoading false on a disabled query), and while it loads the sections would all
    claim to be empty for a beat before filling in.
  */
  if (!hasWebsite)
    return (
      <main className="bg-panel border-line rounded-panel border px-6 py-12 text-center">
        <p className="supporting m-0">Select a website first.</p>
      </main>
    );

  if (isLoading)
    return (
      <main className="bg-panel border-line rounded-panel border px-6 py-12 text-center">
        <p className="supporting m-0">Loading implementations…</p>
      </main>
    );

  /*
    First-run only when the page as a whole is empty — never under the Implemented
    Only switch, which server-filters the fetch: an empty answer to "show me only
    what is marked done" must keep the toolbar carrying that switch, or the reader
    is stranded on an explainer with the control that caused it gone.
  */
  if (visible.length === 0 && !implementedOnly)
    return <ImplementationEmptyState excludedCount={sections.excluded.length} />;

  return (
    <main className="flex flex-col gap-[14px]">
      <ImplementationTopHeader manager={manager} experiments={visible} />

      {sections.excluded.length > 0 && (
        <p className="supporting">
          {sections.excluded.length} finished {sections.excluded.length === 1 ? 'experiment is' : 'experiments are'} not
          listed: no variant beat the control, or the test never gathered enough traffic to tell. The numbers are on
          each experiment&apos;s statistics tab.
        </p>
      )}

      {SECTION_ORDER.map((section) => (
        <SectionTable
          key={section}
          section={section}
          experiments={shown(section)}
          total={sections[section].length}
          searchQuery={searchQuery}
          requestImplemented={requestImplemented}
          onShowCode={showCode}
          anchorFirstRow={section === anchoredSection}
        />
      ))}

      <EndOnImplementDialog experiment={endPrompt} onResolve={resolveEndPrompt} onDismiss={dismissEndPrompt} />

      {codeModalVariant && (
        <CodeModal
          open={!!codeModalVariant}
          onOpenChange={(open) => {
            if (!open) hideCode();
          }}
          variant={codeModalVariant}
        />
      )}
    </main>
  );
};
